'use client';

import { cn } from '@coas/ui/lib';
import { Heart } from 'lucide-react';
import { memo, useCallback } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import { useToggleWishlist, useWishlistIds } from '../model/hooks';

type Props = {
    productId: string;
    className?: string;
    selectedClassName?: string;
    heartClassName?: string;
    selectedHeartClassName?: string;
    unselectedHeartClassName?: string;
    disabled?: boolean;
};

export const ToggleWishlistButton = memo((props: Props) => {
    const {
        productId,
        className,
        selectedClassName,
        heartClassName,
        selectedHeartClassName,
        unselectedHeartClassName,
        disabled,
    } = props;

    const { set, isFetching } = useWishlistIds();
    const isFavourite = set.has(productId);

    const { toggle, isPending } = useToggleWishlist(productId);

    const onClick = useCallback(
        (e: ReactMouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();

            if (isPending || isFetching || disabled) return;
            toggle();
        },
        [isPending, isFetching, disabled, toggle],
    );

    const onKeyDown = useCallback((e: ReactKeyboardEvent<HTMLButtonElement>) => {
        e.stopPropagation();
    }, []);

    return (
        <button
            type='button'
            aria-label={isFavourite ? 'Remove from wishlist' : 'Add to wishlist'}
            aria-pressed={isFavourite}
            onClick={onClick}
            onKeyDown={onKeyDown}
            onPointerDown={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
            disabled={isPending || isFetching || disabled}
            className={cn(
                'inline-flex items-center justify-center rounded-full outline-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-900 transition-opacity',
                isFavourite && selectedClassName,
                className,
            )}
        >
            <Heart
                className={cn(
                    'h-5 w-5',
                    heartClassName,
                    isFavourite ? 'fill-neutral-900 stroke-neutral-900' : 'fill-transparent stroke-neutral-900',
                    isFavourite ? selectedHeartClassName : unselectedHeartClassName,
                )}
            />
        </button>
    );
});
